/**
 * ガチャの実行口(docs/02_game_design.md §7.2)。
 *
 * MVP は `LocalGachaService`(クライアント抽選 + localStorage)。
 * Phase 2 で callable `pullGacha` を叩く `FirebaseGachaService` に**このインターフェースごと**
 * 差し替える(抽選ロジック `logic/gacha.ts` はサーバー実装と同じ確率仕様なので、
 * 差し替えても体験は変わらない)。UI は `GachaService` しか知らない。
 */

import {
  buildPool,
  drawMany,
  gachaCost,
  isGachaOpen,
  resolveDraw,
  type DrawnItem,
  type Rng,
} from '../logic/gacha';
import type { Catalog, GachaDefinition } from './catalog';
import type { InventoryService, InventorySnapshot } from './inventory';
import { ownedIds } from './inventory';
import { InsufficientCoinsError, type CoinWallet } from './progress';
import { TYPING_CURRENCY } from './wallet';

export interface GachaPullInput {
  readonly gachaId: string;
  readonly count: number;
}

export interface GachaPullResult {
  readonly gachaId: string;
  readonly items: readonly DrawnItem[];
  /** 重複ぶんの変換コイン合計。 */
  readonly convertedCoins: number;
  /** 消費コイン。 */
  readonly spent: number;
  /** 抽選後の残高(`currency` の残高)。 */
  readonly balance: number;
  /** 支払いに使った通貨(docs/10 §4.3)。 */
  readonly currency: string;
  /** 抽選後の全通貨の残高(分かるときだけ)。 */
  readonly balances?: Readonly<Record<string, number>>;
  readonly inventory: InventorySnapshot;
}

export class GachaUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GachaUnavailableError';
  }
}

export interface GachaService {
  pull(input: GachaPullInput): Promise<GachaPullResult>;
  /**
   * いまの全通貨の残高(docs/10 §4.3)。**`null` = 分からない**。
   *
   * ローカルモード(Firebase 未設定)は教科コインが存在しないので `null` を返し、
   * 画面は教科ガチャそのものを出さない —— まなびのコイン表示と同じ流儀で、
   * 「絶対に貯まらないコインの 0」を子どもに見せないため。
   */
  balances(): Promise<Readonly<Record<string, number>> | null>;
}

export interface LocalGachaServiceDeps {
  readonly loadCatalog: () => Promise<Catalog>;
  readonly inventory: InventoryService;
  readonly wallet: CoinWallet;
  /** テストで決定的にするため注入可能。 */
  readonly rng?: Rng;
  readonly now?: () => Date;
}

export class LocalGachaService implements GachaService {
  private readonly rng: Rng;
  private readonly now: () => Date;

  constructor(private readonly deps: LocalGachaServiceDeps) {
    this.rng = deps.rng ?? (() => Math.random());
    this.now = deps.now ?? (() => new Date());
  }

  /**
   * ローカルモードには**タイピングのコインしか無い**(教科コインは
   * Cloud Functions が付けるもの)。分からないと答えて、画面に教科ガチャを出させない。
   */
  async balances(): Promise<Readonly<Record<string, number>> | null> {
    return null;
  }

  async pull(input: GachaPullInput): Promise<GachaPullResult> {
    const catalog = await this.deps.loadCatalog();
    const gacha = catalog.gachas.find((g) => g.id === input.gachaId);
    if (!gacha) throw new GachaUnavailableError(`ガチャ ${input.gachaId} がありません`);
    if (gacha.currency !== TYPING_CURRENCY) {
      // 教科コインはサーバーだけが増やせる(CLAUDE.md ルール2)。
      throw new GachaUnavailableError('このガチャは いま ひけません');
    }
    if (!isGachaOpen(gacha, this.now())) {
      throw new GachaUnavailableError('このガチャは いま ひけません');
    }
    assertCount(gacha, input.count);

    const cost = gachaCost(gacha, input.count);
    const balanceBefore = await this.deps.wallet.balance();
    if (balanceBefore < cost) throw new InsufficientCoinsError(cost, balanceBefore);

    // 先に消費してから抽選する(サーバー実装のトランザクション順と同じ)。
    await this.deps.wallet.applyCoinDelta(-cost);

    const getItem = (itemId: string) => catalog.itemsById.get(itemId);
    // 月限定枠(docs/12 §3)は当月ぶんだけ。ローカルモードでも規則をそろえる。
    const pool = buildPool(gacha, getItem, this.now());
    const drawn = drawMany(pool, gacha, input.count, this.rng);
    const owned = ownedIds(await this.deps.inventory.load());
    const resolved = resolveDraw(drawn, owned, getItem, gacha.duplicateConvertCoins);

    const inventory = await this.deps.inventory.grant(
      resolved.newItemIds,
      (itemId) => getItem(itemId)?.slot,
    );
    const balance =
      resolved.convertedCoins > 0
        ? await this.deps.wallet.applyCoinDelta(resolved.convertedCoins)
        : balanceBefore - cost;

    return {
      gachaId: gacha.id,
      items: resolved.items,
      convertedCoins: resolved.convertedCoins,
      spent: cost,
      balance,
      currency: TYPING_CURRENCY,
      inventory,
    };
  }
}

function assertCount(gacha: GachaDefinition, count: number): void {
  if (count !== 1 && count !== gacha.guarantee.count) {
    throw new GachaUnavailableError(`${count} 連は ひけません`);
  }
}
